import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { DialogService } from '@adgate/common/services/dialog-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ProductionService } from '@adgate/modules/production/services/production-service';
import { ProductionRequestFilter } from '@adgate/modules/production/types/production-request-filter.type';
import moment from 'moment';

@Component({
  selector: 'app-production-request-filters-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatAutocompleteModule
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: AdGateConstants.ADGATE_DATE_FORMATS }
  ],
  templateUrl: './production-request-filters-dialog-component.html',
  styleUrl: './production-request-filters-dialog-component.scss',
})
export class ProductionRequestFiltersDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<ProductionRequestFiltersDialogComponent> = inject(MatDialogRef<ProductionRequestFiltersDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private currentFilter: ProductionRequestFilter = inject<any>(MAT_DIALOG_DATA);
  private productionService: ProductionService = inject(ProductionService);
  private dialogService: DialogService = inject(DialogService);

  public comparatorService: ComparatorService = inject(ComparatorService);

  filtersForm!: FormGroup;

  owners: string[] = [];
  filteredOwners!: Observable<string[]>;

  constructor()
  {
      this.filtersForm = this.fb.group({
        client: "",
        name: "",
        requestId: "",
        masterCommercialRefCode: "",
        creatorOwner: "",
        txId: "",
        createdFrom: "",
        createdTo: "",
        resultLimit: 100
      });
  }

  ngOnInit(): void
  {
    this.productionService.getProductionRequestOwners().subscribe({
      next: (response: RESTApplicationResponse) => {
        this.owners = response.data;

        this.filteredOwners = this.filtersForm.get('creatorOwner')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterOwners(value || ''))
        );

        // Initialize form with current filter values
        this.filtersForm.get('client')?.setValue(this.currentFilter.client);
        this.filtersForm.get('name')?.setValue(this.currentFilter.name);
        this.filtersForm.get('requestId')?.setValue(this.currentFilter.requestId);
        this.filtersForm.get('masterCommercialRefCode')?.setValue(this.currentFilter.masterRefCode);
        this.filtersForm.get('creatorOwner')?.setValue(this.currentFilter.creatorName);
        this.filtersForm.get('txId')?.setValue(this.currentFilter.txId);
        this.filtersForm.get('createdFrom')?.setValue(this.currentFilter.createdFrom ? moment(this.currentFilter.createdFrom) : null);
        this.filtersForm.get('createdTo')?.setValue(this.currentFilter.createdTo ? moment(this.currentFilter.createdTo) : null);
        this.filtersForm.get('resultLimit')?.setValue(this.currentFilter.limit);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private _filterOwners(value: string): string[]
  {
    const filterValue = value.toLowerCase();
    return this.owners.filter(owner => owner.toLowerCase().includes(filterValue));
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  onClear(): void
  {
    this.filtersForm.reset({ resultLimit: 100 });
  }

  onApply(): void
  {
    this.currentFilter.client = this.filtersForm.get('client')?.value;
    this.currentFilter.name = this.filtersForm.get('name')?.value;
    this.currentFilter.requestId = this.filtersForm.get('requestId')?.value;
    this.currentFilter.masterRefCode = this.filtersForm.get('masterCommercialRefCode')?.value;
    this.currentFilter.creatorName = this.filtersForm.get('creatorOwner')?.value;
    this.currentFilter.txId = this.filtersForm.get('txId')?.value;
    this.currentFilter.limit = this.filtersForm.get('resultLimit')?.value;

    const createdFrom = this.filtersForm.get('createdFrom')?.value;
    const createdTo = this.filtersForm.get('createdTo')?.value;
    this.currentFilter.createdFrom = createdFrom ? createdFrom.unix() * 1000 : null;
    this.currentFilter.createdTo = createdTo ? createdTo.unix() * 1000 : null;

    this.dialogRef.close(this.currentFilter);
  }
}
